<template>
  <main
    v-show="isActive"
    v-if="campsite"
    class="page-content campsite"
    :class="{ desktop: $isDesktop }"
    data-test="campsite-details-wrapper"
  >
    <AsyncCampsiteBreadcrumbs
      v-if="$isDesktop"
      :key="getAsyncComponentKey('campsiteBreadcrumbsHeader')"
      :categories="campsiteCategories"
      :hierarchy-breadcrumbs="campsite.hierarchyBreadcrumbs"
      :with-counts="false"
      :campsite-detail-url="route.fullPath"
      :campsite-name="campsite?.name"
      class="campsite-header-breadcrumbs"
      @vue:mounted="setAsyncComponentKey('campsiteBreadcrumbsHeader')"
    />

    <AsyncSearchSummaryBar
      v-if="!$isDesktop"
      :key="getAsyncComponentKey('searchSummaryBar')"
      v-bind="searchSummaryBarProps"
      @vue:mounted="setAsyncComponentKey('searchSummaryBar')"
    />

    <client-only>
      <AsyncFavouritesOnboarding
        v-if="onboardingStore.shouldShowFavouritesOnboarding"
        :key="getAsyncComponentKey('favouritesOnboarding')"
        @vue:mounted="setAsyncComponentKey('favouritesOnboarding')"
      />
    </client-only>
    <CampsiteDetailBookableListing
      v-if="campsite.isBookable"
      v-bind="bookableListingProps"
      @rangeChanged="rangeChanged"
      @partyChanged="partyChanged"
      @clearDates="clearDates"
      @pts-viewed="ptsViewedChanged"
    />
    <AsyncCampsiteDetailFreeListing
      v-else
      :campsite="campsite"
      :search-filters="searchFilters"
      :is-dated="isDated"
      :types="types"
    />

    <AsyncCampsiteDetailFaq
      v-if="campsite.faq"
      :key="getAsyncComponentKey('campsiteDetailFaq')"
      :faq="campsite.faq"
      class="campsite-subsection"
      @vue:mounted="setAsyncComponentKey('campsiteDetailFaq')"
    />

    <AsyncCampsiteBreadcrumbs
      :key="getAsyncComponentKey('campsiteBreadcrumbsFooter')"
      :categories="campsiteCategories"
      :hierarchy-breadcrumbs="campsite.hierarchyBreadcrumbs"
      :with-counts="false"
      :campsite-detail-url="route.fullPath"
      :campsite-name="campsite.name"
      :remove-filters="['sort']"
      class="campsite-footer-breadcrumbs campsite-subsection"
      @vue:mounted="setAsyncComponentKey('campsiteBreadcrumbsFooter')"
    />

    <span class="campsite-subsection">
      <AsyncSingleCategoryBreadcrumbs
        v-for="category in campsiteCategoryObjects"
        :key="getSingleCategoryBreadcrumbKey(category.id)"
        :category="category"
        :hierarchy-breadcrumbs="campsite.hierarchyBreadcrumbs"
        :hierarchy-path="campsite.path"
        :campsite-detail-url="route.fullPath"
        :campsite-name="campsite.name"
        @vue:mounted="setSingleCategoryBreadcrumbKey(category.id)"
      />
    </span>

    <AsyncCampsiteDetailBackButton
      :key="getAsyncComponentKey('campsiteDetailBackButton')"
      :hierarchy-path="campsite.path"
      :hierarchy-text="campsite.hierarchyTextShort"
      :referrer-route="searchReferrerRoute"
      :pts-viewed="ptsViewed"
      class="campsite-subsection"
      @vue:mounted="setAsyncComponentKey('campsiteDetailBackButton')"
    />

    <AsyncBackToTop
      :key="getAsyncComponentKey('backToTop')"
      @vue:mounted="setAsyncComponentKey('backToTop')"
    />
  </main>
</template>
<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-misused-promises */
/* TODO: Add proper typing for campsite data structures and state machines */
// Vue/Nuxt core imports
import { useAsyncData, useHead, useRuntimeConfig } from 'nuxt/app'
import { isEqual } from 'lodash-es'
import {
  computed,
  defineAsyncComponent,
  hydrateOnIdle,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

// Component imports
import CampsiteDetailBookableListing from '~/apps/campsite-details/components/CampsiteDetailBookableListing.vue'

// App-specific imports
import { getChargeTypesStateMachine } from '~/apps/campsite-details/inversify.config'
import type {
  ChargeTypesContext,
  ChargeTypesMachineInterpreter,
} from '~/apps/campsite-details/machines'
import { buildHotelPriceSchema } from '~/apps/campsite-details/hotelPriceSchema'
import type { CampsiteDetails } from '~/apps/campsite-details/types'
import {
  ChargeTypesInput,
  PartyChangedEvent,
  PartyTransformedEvent,
  RangeChangedEvent,
} from '~/apps/campsite-details/types'
import { ReviewsRepositoryFetch } from '~/apps/campsites/reviews/ReviewsRepositoryFetch'
import type { Review } from '~/apps/campsites/reviews/types'
import { SearchFiltersUrl } from '~/apps/url/search-filters/SearchFiltersUrl'
import { ensureArray } from '~/utility/array'
import { DateRange } from '~/utility/date/DateRange'
import { isValidDate } from '~/utility/date/isValidDate'
import { addDays } from '~/utility/date/relative'
import { getHttpHeaders } from '~/utility/headers'
import { getCampsiteCategoriesPresenter } from '../../apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { isSearchUrlRoute } from '../../apps/search/isSearchUrlComponent'
import { childAgeCSVStringToNumberArray } from '../../utility/child-ages/child-ages'
import { getPointFromStringOrDefault } from '../../utility/geo/latLng'
import { transformImageSrcSet } from '../../utility/image-url'
import { logger } from '../../utility/logger'
import { getYearForTitleDate } from '../../utility/next-year'
import { waitForInterpreterState } from '../../utility/xstate/waitForInterpreterState'
import { hydrateOnVisibleSafe } from '../../utility/hydration/hydrateOnVisibleSafe'
import { yieldToMain } from '../../utility/scheduling'
import { campsiteFetchingPageAsyncData } from './CampsiteFetchingPage'

// Define async components with hydration strategies
const AsyncCampsiteBreadcrumbs = defineAsyncComponent({
  loader: () => import('../breadcrumbs/CampsiteBreadcrumbs.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncSearchSummaryBar = defineAsyncComponent({
  loader: () => import('../search/summary-bar/SearchSummaryBar.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncFavouritesOnboarding = defineAsyncComponent({
  loader: () =>
    import('../../apps/favourites/components/common/FavouritesOnboarding.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncCampsiteDetailFreeListing = defineAsyncComponent({
  loader: () =>
    import(
      '../../apps/campsite-details/components/CampsiteDetailFreeListing.vue'
    ),
  hydrate: hydrateOnIdle(10000),
})

const AsyncCampsiteDetailFaq = defineAsyncComponent({
  loader: () =>
    import('../../apps/campsite-details/components/CampsiteDetailFaq.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncSingleCategoryBreadcrumbs = defineAsyncComponent({
  loader: () => import('../breadcrumbs/SingleCategoryBreadcrumbs.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncCampsiteDetailBackButton = defineAsyncComponent({
  loader: () =>
    import(
      '../../apps/campsite-details/components/CampsiteDetailBackButton.vue'
    ),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncBackToTop = defineAsyncComponent({
  loader: () => import('../ui/BackToTop.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

// Store imports
const pinia = usePinia()
const seenCampsitesStore = useSeenCampsitesStore(pinia)
const recentCampsitesStore = useRecentCampsitesStore(pinia)
const onboardingStore = useOnboardingStore(pinia)
const facetsStore = useFacetsStore(pinia)

const route = useRoute()
const router = useRouter()
const isActive = ref(true)
const isUnmounted = ref(false)
const ptsViewed = ref(0)
const review = ref<Review | null>(null)

// Dynamic keys for async components
const asyncComponentKeys = ref({})

// Helper functions for async component keys
const getAsyncComponentKey = (componentName: string) => {
  return (
    asyncComponentKeys.value[componentName] ||
    `${route.fullPath.split('#')[0]}-${componentName}`
  )
}
const setAsyncComponentKey = (componentName: string) => {
  if (isUnmounted.value) return
  asyncComponentKeys.value[componentName] =
    `${route.fullPath.split('#')[0]}-${componentName}`
}

const getSingleCategoryBreadcrumbKey = (categoryId: string) => {
  return (
    asyncComponentKeys.value[`singleCategoryBreadcrumbs_${categoryId}`] ||
    `${route.fullPath}_${categoryId}`
  )
}

const setSingleCategoryBreadcrumbKey = (categoryId: string) => {
  if (isUnmounted.value) return
  asyncComponentKeys.value[`singleCategoryBreadcrumbs_${categoryId}`] =
    `${route.fullPath}_${categoryId}`
}

const chargeTypesStateStrings = ref<string[]>([])
// shallowRef: context is always replaced wholesale (never mutated in-place) via
// state.context in onTransition. Avoids deep proxy creation for nested chargetypes
// data (7 pitchtypes × multiple fields), reducing Vue micro-task overhead during
// the reactive cascade on date selection. (P3 INP fix)
const chargeTypesContext = shallowRef<ChargeTypesContext | null>(null)
const lastFetchParams = ref<ReturnType<
  typeof SearchFiltersUrl.urlToFilters
> | null>(null)
const minDays = ref(1)
const { $puScrollTo, $isDesktop, $gtm, $t, $routerClient } = useNuxtApp()
const _chargeTypesStateMachine = ref<ChargeTypesMachineInterpreter | null>(null)
const langCode = (route?.params?.lang as string) || 'en-gb'

// Track current state machine for cleanup (non-reactive to avoid SSR serialization issues)
// IMPORTANT: Must be declared before onBeforeUnmount to avoid Temporal Dead Zone errors
let currentChargeTypesStateMachine: any = null
const categoryMap = getCampsiteCategoriesPresenter(langCode)
const runtimeConfig = useRuntimeConfig()
const pitchtypeId = ref(route?.query?.pitchtype as string)

// Define page metadata
definePageMeta({
  scrollToTop: true,
  type: 'campsite-details',
  httpHeaders: (to) => getHttpHeaders({ to }),
})

// Lifecycle hooks
// Initialization
onMounted(async () => {
  if (reviewId.value) {
    await setUpReview(reviewId.value)
  }
  // Handle initial route
  if (
    route.hash.startsWith('#reviews') &&
    !route.hash.startsWith('#reviews_')
  ) {
    setTimeout(() => {
      if (!isUnmounted.value) {
        $puScrollTo('[data-reviews-scroll-target]', { duration: 300 })
      }
    }, 500)
  }
  // note onActivated does not fire
  // so joining it on end of mounted
  // })

  // onActivated(() => {
  isActive.value = true
  start()
})

onBeforeUnmount(() => {
  isUnmounted.value = true

  if (currentChargeTypesStateMachine && currentChargeTypesStateMachine.stop) {
    currentChargeTypesStateMachine.stop()
  }
  // note onDeactivated does not fire
  // so joining it on end of beforeUnmount
  // })

  // onDeactivated(() => {
  if (isActive && typeof isActive.value !== 'undefined') {
    isActive.value = false
  }
  if (lastFetchParams) {
    lastFetchParams.value = null
  }
})

// Replace asyncData with useAsyncData
interface AsyncDataResult {
  campsite: CampsiteDetails
  loading: boolean
  chargeTypesContext?: ChargeTypesContext | null
  chargeTypesStateStrings?: string[]
  lastFetchParams?: any
}

const { data, error } = await useAsyncData<AsyncDataResult>(
  `campsite-detail-${route.params.slug}-${ensureArray(route.query.facet)
    .sort()
    .join(',')}`,
  async (nuxtApp) => {
    await facetsStore.getFacetGroups(langCode)
    const currentFacets = ensureArray(route.query.facet)
    const globalPitchtypeFacets = facetsStore.pitchtypeFacetSlugs

    // Unified validation logic
    const filteredFacets = currentFacets.filter((f) =>
      globalPitchtypeFacets.includes(f as string),
    )

    if (currentFacets.length !== filteredFacets.length) {
      const newQuery = { ...route.query }
      if (filteredFacets.length) {
        newQuery.facet = filteredFacets
      } else {
        delete newQuery.facet
      }

      const redirectOptions = process.server
        ? { redirectCode: 301 }
        : { replace: true }

      await nuxtApp?.runWithContext(async () => {
        await navigateTo(
          {
            path: route.path,
            query: newQuery,
          },
          redirectOptions,
        )
      })

      if (process.server) return null
    }

    const campsitePromise = campsiteFetchingPageAsyncData({
      route: {
        params: { slug: route.params.slug as string, lang: langCode },
        query: route.query,
        path: route.path,
        fullPath: route.fullPath,
        meta: route.meta,
      },
      error: (params) => {
        throw createError(params)
      },
      async redirect(code, url) {
        return await nuxtApp?.runWithContext(async () => {
          await navigateTo(url, { redirectCode: code })
        })
      },
      defaultAdultsCount: 2,
    }) as Promise<AsyncDataResult>

    // Start charge types fetch in parallel if needed
    let chargeTypesPromise: Promise<any> | null = null
    if (route.query.arrive && isValidDate(route.query.arrive as string)) {
      const query = queryWrapper(route.query)
      const params = {
        campsiteSlug: route.params.slug as string,
        arrive: query.arrive,
        depart: query.depart || null,
        adults: query.adults ? Number(query.adults) : null,
        children: query.child_ages ? query.child_ages.split(',').length : 0,
        childAges: query.child_ages || null,
        dogs: query.dogs ? Number(query.dogs) : null,
        nights: query.nights ? Number(query.nights) : null,
        availability: true,
      }
      chargeTypesPromise = getChargeTypesState(params, langCode, nuxtApp)
    }

    // Wait for both promises in parallel
    const [result, chargeTypesData] = await Promise.all([
      campsitePromise,
      chargeTypesPromise || Promise.resolve(null),
    ])

    // Return combined result
    if (chargeTypesData) {
      return {
        ...result,
        ...chargeTypesData,
        lastFetchParams: SearchFiltersUrl.urlToFilters({ ...route.query }),
      }
    }

    return result
  },
  {
    server: true,
    lazy: false,
  },
)

if (error.value) {
  throw createError({
    statusCode: 404,
    statusMessage: `Page Not Found campsite detail ${route.params.slug}`,
    fatal: process.client, // Only set fatal true on client-side
  })
}

watch(
  () => data.value,
  (newData) => {
    if (newData) {
      chargeTypesContext.value = newData.chargeTypesContext || null
      chargeTypesStateStrings.value = newData.chargeTypesStateStrings || []
      lastFetchParams.value = newData.lastFetchParams || null
    }
  },
  { immediate: true },
)

const campsite = computed(() => data.value?.campsite)

watch(
  () => route.query,
  (newQuery, oldQuery) => {
    logger('CampsiteDetail.route.query', newQuery, oldQuery)
    if (isDated.value && !isEqual(newQuery, oldQuery) && isActive.value) {
      void startChargeTypesStateMachine()
    }
    if (pitchtypeId.value !== newQuery.pitchtype) {
      pitchtypeId.value = newQuery.pitchtype as string
    }
  },
  { deep: true },
)

// Methods
const start = () => {
  if (isDated.value) {
    void startChargeTypesStateMachine()
  }

  // Page view tracking
  if (process.client) {
    const prefix = route.params.lang ? `/${route.params.lang}` : ''
    const metaData = new URLSearchParams({
      isDesktop: $isDesktop.toString(),
      alfred: 'true',
      alfredVersion: runtimeConfig.public.alfredVersion,
      isDated: ('arrive' in route.query).toString(),
      path: route.fullPath,
    })

    const url = `/_/fallback${prefix}/pagestat/campsite/${data.value?.campsite?.id}/page_view/`

    try {
      if (navigator.sendBeacon) {
        const sent = navigator.sendBeacon(url, metaData)
        if (!sent) {
          throw new Error('sendBeacon returned false')
        }
      } else {
        throw new Error('sendBeacon not available')
      }
    } catch {
      $fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: metaData,
      }).catch(() => {})
    }

    requestIdleCallback(async () => {
      if (!isUnmounted.value) {
        await addCampsiteToRecent()
      }
    })

    if (data.value?.campsite?.slug) {
      seenCampsitesStore.add(data.value?.campsite?.slug)
    }
  }

  // Add GTM tracking
  $gtm.push({
    pageCategories: data.value?.campsite?.categories
      .map((category) => category.slug)
      .join(','),
  })
  const product: any = {}
  // Add content_ids only if campsite is bookable (for GTM tracking)
  if (data.value?.campsite?.isBookable && data.value?.campsite?.id) {
    product.content_ids = [data.value.campsite.id]
  }
  if (route.query.arrive) {
    product.checkin_date = route.query.arrive
  }
  if (route.query.depart) {
    product.checkout_date = route.query.depart
  }
  if (route.query.adults) {
    product.num_adults = route.query.adults
  }
  if (route.query.child_ages) {
    product.num_children = route.query.child_ages.split(',').length
  }
  $gtm.push({ event: 'campsite_detail', product })

  // Handle review scroll if needed
  if (
    route.hash.startsWith('#reviews') &&
    !route.hash.startsWith('#reviews_')
  ) {
    setTimeout(() => {
      if (!isUnmounted.value) {
        $puScrollTo('[data-reviews-scroll-target]', { duration: 300 })
      }
    }, 500)
  } else {
    if (route.query.scroll_to_pitchtype) {
      setTimeout(() => {
        if (!isUnmounted.value) {
          $puScrollTo('#pitch-types', { duration: 300 })
        }
      }, 500)
    }
  }
}

// Computed properties
const isDated = computed(() => !!route.query?.arrive)

const searchFilters = computed(() =>
  SearchFiltersUrl.urlToFilters({ ...route.query }),
)
const bookableListingProps = computed(() => {
  const pitchtypeFacets = data.value?.campsite?.pitchtypeFacets || []
  const facets = route.query.facet
    ? ensureArray(route.query.facet).filter(
        (f) => !!f && pitchtypeFacets.includes(f),
      )
    : []
  return {
    campsite: data.value?.campsite,
    categoryIds: categoryIds.value,
    isDated: isDated.value,
    hasAvailability: hasAvailability.value,
    party: party.value,
    chargetypes: chargetypes.value,
    types: types.value,
    dateRange: dateRange.value,
    pitchtypeId: pitchtypeId.value,
    scrollToPitchtypeId: route.query.scroll_to_pitchtype as string,
    searchFilters: searchFilters.value,
    previousPageWasCheckAvailability: previousPageWasCheckAvailability.value,
    reviewId: reviewId.value,
    chargeTypesUpdating: chargeTypesUpdating.value,
    breadcrumbs: data.value?.campsite?.breadcrumbs,
    facets: facets as string[],
  }
})

const campsiteSlug = computed(() => data.value?.campsite?.slug)

const chargeTypesUpdating = computed(() => {
  if (!route.query.arrive) {
    return false
  }
  return chargeTypesStateStrings.value.length
    ? chargeTypesStateStrings.value.includes('fetchingChargeTypes')
    : true
})

// Event handlers
const rangeChanged = async (dates: RangeChangedEvent) => {
  const cloneDates: any = { ...dates }
  if (dateRange.value.start === dates.arrive) {
    delete cloneDates.arrive
  }
  if (dateRange.value.end === dates.depart) {
    delete cloneDates.depart
  }
  if (Object.keys(cloneDates).length) {
    // Yield so the browser paints the calendar's visual update (handled by
    // Flatpickr) before the route change triggers the heavy reactive cascade
    // (deep route.query watcher → charge types state machine). Without this,
    // the synchronous router.push + watcher processing adds ~290ms to INP.
    await yieldToMain()
    if (isUnmounted.value) return
    await updateRouteQueryParams(cloneDates)
  }
}

const partyChanged = async (party: PartyChangedEvent) => {
  const params: PartyTransformedEvent = { adults: party.adults }
  if (party.children && party.children > 0) {
    params.children = String(party.children)
    params.child_ages = party.childAges?.length
      ? party.childAges?.join(',')
      : ''
  } else {
    params.children = undefined
    params.child_ages = undefined
  }
  if (party.dogs && party.dogs > 0) {
    params.dogs = String(party.dogs)
  } else {
    params.dogs = undefined
  }
  // Same yield pattern as rangeChanged — let the UI update before
  // the heavy route change cascade.
  await yieldToMain()
  if (isUnmounted.value) return
  await updateRouteQueryParams(params)
}

// Watch for search filters changes
watch(
  () => searchFilters.value,
  (newVal, oldVal) => {
    if (isDated.value && !isEqual(newVal, oldVal) && isActive.value) {
      void startChargeTypesStateMachine()
    }
  },
)

// Additional computed properties
const streetAddress = computed(() => {
  const address2 = data.value?.campsite?.address2
    ? data.value.campsite.address2
    : ''
  if (address2) {
    return `${data.value?.campsite?.address1}, ${address2}`
  }
  return data.value?.campsite?.address1
})

const categoryIds = computed(() =>
  (searchFilters.value.categoryIds || []).filter((catId) =>
    data.value?.campsite?.categories
      .map((category) => category.id)
      .includes(catId),
  ),
)

const hasAvailability = computed(() => {
  const availability = chargeTypesContext.value?.hasAvailability
  const availabilitySet = availability !== undefined
  return availabilitySet ? !!availability : null
})

const chargetypes = computed(() => chargeTypesContext.value?.chargetypes || [])

const dateRange = computed(() => ({
  start: searchFilters.value.dates?.arrive || '',
  end: searchFilters.value.dates?.depart || '',
}))

const childAges = computed(() =>
  route.query.child_ages
    ? childAgeCSVStringToNumberArray(route.query.child_ages as string)
    : [],
)

const party = computed(() => ({
  adults: adults.value,
  childAges: childAges.value,
  dogs: dogs.value,
}))

const types = computed(() =>
  ensureArray(route.query.type).map((type) => parseInt(type as string, 10)),
)

const adults = computed(() => {
  const partyValue = searchFilters.value.party
  return partyValue ? partyValue.adults : 2
})

const dogs = computed(() => {
  const partyValue = searchFilters.value.party
  return partyValue ? partyValue.dogs : undefined
})

const searchSummaryBarProps = computed(() => ({
  searchFilters: searchFilters.value,
  searchReferrerRoute: searchReferrerRoute.value,
  campsiteSlug: data.value?.campsite?.slug,
  ptsViewed: ptsViewed.value,
}))

const searchReferrerRoute = computed(() => {
  const referrer = $routerClient.getReferrer()
  if (referrer && isSearchUrlRoute(referrer)) {
    return referrer
  }
  return null
})

const previousPageWasCheckAvailability = computed(
  () =>
    $routerClient.getReferrer()?.name === 'campsite-slug-check-availability',
)

const campsiteCategories = computed(() =>
  data.value?.campsite?.categories.map((c) => c.id.toString()),
)

const reviewId = computed(() => {
  const reviewFromQuery = ensureArray(
    route.query.review || route.query.review_posted,
  )[0]
  return reviewFromQuery || undefined
})

const potentialAction = computed(() => ({
  '@type': 'ReserveAction',
  target: {
    '@type': 'EntryPoint',
    urlTemplate: `https://${runtimeConfig.public.envUrl}${route.fullPath}`,
    inLanguage: data.value?.campsite?.locale,
    actionPlatform: [
      'http://schema.org/MobileWebPlatform',
      'http://schema.org/DesktopWebPlatform',
    ],
  },
  result: {
    '@type': 'LodgingReservation',
    name: $t('Booking at {campsiteName}', {
      campsiteName: data.value?.campsite?.name,
    }),
  },
}))

// Methods
const getCategory = (id: string) => {
  if (id === 'glamping') {
    return {
      rootSlug: 'glamping',
      breadcrumbName: $t('Glamping sites'),
      id: 'glamping',
    }
  }
  return {
    ...categoryMap.byIdMap[id],
    id,
  }
}

const campsiteCategoryObjects = computed(() => {
  const uniqueCategoryIds = Array.from(
    new Set(
      data.value?.campsite?.pitchtypes.flatMap((pt) =>
        pt.categories.map((c) => c.id.toString()),
      ) || [],
    ),
  )
  let categoriesToUse =
    uniqueCategoryIds.length > 0
      ? uniqueCategoryIds
      : data.value?.campsite?.categories.map((c) => c.id.toString()) || []

  categoriesToUse = categoriesToUse.filter((id) => id in categoryMap.byIdMap)

  if (categoriesToUse.includes('7') || categoriesToUse.includes('13')) {
    categoriesToUse.push('glamping')
  }

  categoriesToUse = categoriesToUse.filter((id) => id !== '13')

  // Sort the categories by ID to ensure consistent order
  return categoriesToUse.sort((a, b) => a.localeCompare(b)).map(getCategory)
})

async function clearDates() {
  if (route.query.arrive || route.query.depart) {
    const query = {
      ...route.query,
      arrive: undefined,
      depart: undefined,
      max_price: undefined,
      min_price: undefined,
    }
    await router.push({ query })
  }
}

async function updateRouteQueryParams(params) {
  await router.push({
    query: removeEmptyQueryParams({ ...route.query, ...params }),
  })
}

const removeEmptyQueryParams = (params) => {
  return Object.entries(params).reduce((query, entry) => {
    if (entry[1] || entry[0] === 'adults') {
      query[entry[0]] = entry[1]
    }
    return query
  }, {})
}

// State machine related methods
async function startChargeTypesStateMachine() {
  const routeParams = SearchFiltersUrl.urlToFilters({ ...route.query })
  if (isEqual(routeParams, lastFetchParams.value)) {
    return
  }
  lastFetchParams.value = routeParams

  // Yield to main thread so the browser can paint the user's interaction
  // (e.g. date selection) before starting the heavy charge types fetch cascade.
  // The state machine triggers loadComponentData for each pitch type (~7),
  // which causes ~160ms of synchronous processing. (P3 INP fix)
  await yieldToMain()

  // Guard against unmount or superseded params after yield
  if (isUnmounted.value) return
  if (!isEqual(lastFetchParams.value, routeParams)) return

  // Stop any existing state machine before starting a new one
  if (currentChargeTypesStateMachine && currentChargeTypesStateMachine.stop) {
    currentChargeTypesStateMachine.stop()
  }

  const chargeTypesStateMachine = getChargeTypesStateMachine(
    langCode,
  ).onTransition((state) => {
    if (isUnmounted.value) return
    chargeTypesStateStrings.value = state.toStrings()
    chargeTypesContext.value = state.context
    // scroll to pitchtype if we have results and pitchtype isn't open
    if (
      state.matches('success') &&
      state.context.hasAvailability &&
      !pitchtypeId.value
    ) {
      requestAnimationFrame(() => {
        if (!isUnmounted.value) {
          $puScrollTo('[data-pitchtypes-scroll-target]', {
            duration: 300,
            // height of sticky navbar to prevent overlap
            offset: -73,
          })
        }
      })
    }
  })

  // Track the current state machine for cleanup
  currentChargeTypesStateMachine = chargeTypesStateMachine

  chargeTypesStateMachine.start()
  fetchChargeTypes(chargeTypesStateMachine)
}

const fetchChargeTypes = (
  chargeTypesStateMachine: ChargeTypesMachineInterpreter,
) => {
  const arrive = dateRange.value.start
  const depart = dateRange.value.end
    ? dateRange.value.end
    : addDays(minDays.value, dateRange.value.start)
  const nights = new DateRange(arrive, depart).nights
  const params: ChargeTypesInput = {
    campsiteSlug: data.value?.campsite?.slug,
    arrive,
    depart,
    adults: party.value.adults,
    children: party.value.childAges.length,
    childAges: party.value.childAges.join(','),
    dogs: party.value.dogs,
    nights,
    availability: true,
  }
  chargeTypesStateMachine.send({
    type: 'FETCH_CHARGETYPES',
    pitchtypes: data.value?.campsite?.pitchtypes,
    params,
  })
}

const ptsViewedChanged = (number: number) => {
  ptsViewed.value = number
}

const addCampsiteToRecent = async () => {
  if (data.value?.campsite?.id) {
    await recentCampsitesStore.addCampsite(data.value.campsite.id)
  }
}

// Utility functions
function queryWrapper(query: any) {
  const ret: Record<string, string> = {}
  for (const key in query) {
    const val = ensureArray(query[key])
    if (val.length === 1 && val[0] !== null) {
      ret[key] = val[0]
    }
  }
  return ret
}

// seo

const getOgUrl = computed(() => {
  return review.value
    ? `${data.value?.campsite?.og_url}?review=${review.value.id}`
    : data.value?.campsite?.og_url
})

const getOgDescription = computed(() => {
  return (
    review.value
      ? getReviewOgDescription.value
      : (data.value?.campsite?.og_description ??
        data.value?.campsite?.description ??
        '')
  ).slice(0, 300)
})

const hotelPriceSchema = computed(() => {
  const cs = campsite.value
  if (!cs) return null
  return buildHotelPriceSchema({
    campsite: cs,
    streetAddress: streetAddress.value,
    isDated: isDated.value,
    chargeTypesUpdating: chargeTypesUpdating.value,
    dateRange: dateRange.value,
    chargetypes: chargetypes.value,
    pitchtypeId: pitchtypeId.value,
  })
})

const head = computed(() => {
  const campsiteName = campsite.value?.name
  const town = campsite.value?.hierarchyTextShort
  const point = getPointFromStringOrDefault(campsite.value?.point)
  let title = ''
  if (campsite.value?.isBookable) {
    title =
      $t(
        '{campsiteName}, {town} - Updated {year, puDate, year:numeric} prices',
        { campsiteName, town, year: getYearForTitleDate() },
      ) + ' | Pitchup.com'
  } else {
    title =
      $t('{campsiteName}, {town}', {
        campsiteName,
        town,
      }) + ' | Pitchup.com'
  }
  let primaryPhoto = ''
  if (campsite.value?.primaryPhoto?.url.masterImage) {
    primaryPhoto = campsite.value?.primaryPhoto?.url.masterImage
  } else {
    primaryPhoto = `https://${runtimeConfig.public.envUrl}/app-icons/pitchup_logo_og.png`
  }
  let aggregateRating = {}
  if (campsite.value?.rateCount && campsite.value?.rating) {
    aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: parseFloat((campsite.value?.rating * 2).toFixed(1)),
      reviewCount: campsite.value?.rateCount,
      worstRating: '1',
      bestRating: '10',
    }
  }

  // Prepare image srcset for preloading if primary photo exists
  const link: Array<{
    rel: string
    as: string
    fetchpriority: string
    imagesrcset?: string
    imagesizes?: string
  }> = []
  if (campsite.value?.primaryPhoto?.url.masterImage) {
    const imgUrl = campsite.value.primaryPhoto.url.masterImage
    const srcSet = `
      /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${imgUrl} 360w,
      /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=720,h=540${imgUrl} 840w,
      /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=1080,h=810${imgUrl} 1080w
    `
    link.push({
      rel: 'preload',
      as: 'image',
      fetchpriority: 'high',
      imagesrcset: transformImageSrcSet(srcSet),
      imagesizes: $isDesktop ? '45vw' : '100vw',
    })
  }

  return {
    title,
    meta: [
      {
        hid: 'og:title',
        property: 'og:title',
        content: review.value ? getReviewOgTitle.value : title,
      },
      {
        hid: 'og:url',
        property: 'og:url',
        content: getOgUrl.value,
      },
      {
        hid: 'description',
        name: 'description',
        property: 'og:description',
        content: getOgDescription.value,
        'data-brainlabs-price':
          campsite.value?.isBookable && campsite.value?.leadPrice
            ? `${campsite.value?.leadPrice?.currency} ${campsite.value?.leadPrice?.amount}`
            : '',
      },
      ...flatten(
        campsite.value?.seoPhotos?.slice(0, 5).map((photo, index) => {
          const photos = [
            {
              hid: `og:image${index > 0 ? index : ''}`,
              property: 'og:image',
              content: `https://${runtimeConfig.public.envUrl}${photo.url}`,
            },
            {
              hid: `og:image${index > 0 ? index : ''}:width`,
              property: 'og:image:width',
              content: `${photo.width}`,
            },
            {
              hid: `og:image${index > 0 ? index : ''}:height`,
              property: 'og:image:height',
              content: `${photo.height}`,
            },
          ]
          if (photo.caption) {
            photos.push({
              hid: `og:image${index > 0 ? index : ''}:alt`,
              property: 'og:image:alt',
              content: photo.caption,
            })
          }
          return photos
        }) || [],
      ),
    ],
    link,
    htmlAttrs: {
      prefix:
        'og: http://ogp.me/ns# fb: http://ogp.me/ns/fb# pitchup: http://ogp.me/ns/fb/pitchup#',
    },
    script: [
      {
        hid: 'ldjson-schema-campsite-detail',
        type: 'application/ld+json',
        body: true,
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': campsite.value?.path?.includes('Wales/Mid-Wales/Ceredigion')
            ? 'Campground'
            : 'LodgingBusiness',
          name: campsiteName,
          description: campsite.value?.description,
          url: `https://${runtimeConfig.public.envUrl}${route.fullPath}`,
          address: {
            '@type': 'PostalAddress',
            streetAddress: streetAddress.value,
            addressLocality:
              (campsite.value?.hierarchy && campsite.value?.hierarchy.town) ||
              '',
            addressRegion: campsite.value?.countyOrRegion || '',
            postalCode: campsite.value?.postcode,
            addressCountry: campsite.value?.countryIso || '',
          },
          aggregateRating,
          image: primaryPhoto,
          availableLanguage: campsite.value?.languages.map((language) => {
            const obj: any = {
              '@type': 'Language',
              name: language.name,
            }
            if (language.code) {
              obj.alternateName = language.code
            }
            return obj
          }),
          priceRange: campsite.value?.seoPriceRange,
          amenityFeature: {
            '@type': 'LocationFeatureSpecification',
            name: 'instantBookable',
            value: true,
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: point.lat,
            longitude: point.lng,
          },
          potentialAction: campsite.value?.isBookable
            ? potentialAction.value
            : '',
        }),
      },
      ...(hotelPriceSchema.value
        ? [
            {
              hid: 'ldjson-schema-hotel-price',
              type: 'application/ld+json',
              body: true,
              innerHTML: JSON.stringify(hotelPriceSchema.value),
            },
          ]
        : []),
      ...(campsite.value?.seoPhotos?.map((photo, index) => ({
        hid: 'ldjson-schema-campsite-detail-photo-' + index,
        type: 'application/ld+json',
        body: true,
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ImageObject',
          name: campsite.value?.name,
          contentLocation: `${campsite.value?.name}, ${campsite.value?.hierarchyTextShort}`,
          contentUrl: `https://${runtimeConfig.public.envUrl}${photo.url}`,
          description: photo.caption,
        }),
      })) || []),
    ],
  }
})

function flatten<T>(arr: T[][]): T[] {
  return ([] as T[]).concat(...arr)
}

useHead(head.value)

onBeforeRouteUpdate((to, from) => {
  if (to.params.id !== from.params.id) {
    start()
  }
})

async function getChargeTypesState(
  params: ChargeTypesInput,
  langCode: string,
  nuxtApp?: ReturnType<typeof useNuxtApp>,
) {
  const chargeTypesStateMachine = getChargeTypesStateMachine(
    langCode,
    nuxtApp,
  ).start()
  chargeTypesStateMachine.send({
    type: 'FETCH_CHARGETYPES',
    pitchtypes: [],
    params,
  })
  await waitForInterpreterState({
    machine: chargeTypesStateMachine,
    successStates: ['success'],
    errorStates: ['error'],
  }).catch((error) => {
    console.error('Error fetching charge types', error)
  })
  chargeTypesStateMachine.stop()
  const chargeTypesStateStrings = chargeTypesStateMachine.state.toStrings()
  const chargeTypesContext = chargeTypesStateMachine.state.context
  return { chargeTypesContext, chargeTypesStateStrings }
}

// Add setUpReview function
const setUpReview = async (reviewId: string) => {
  const repo = new ReviewsRepositoryFetch()
  try {
    review.value = await repo.readOneById(langCode, {
      campsiteSlug: campsiteSlug.value,
      id: reviewId,
    })
  } catch (error) {
    console.error('Error fetching review:', error)
  }
}

// Add review-related computed properties for meta tags
const getReviewOgTitle = computed(() => {
  return $t("{username}'s review of {campsite} on Pitchup.com", {
    username: review.value?.user?.username,
    campsite: data.value?.campsite?.name,
  })
})

const getReviewOgDescription = computed(
  () => `${review.value?.title} ${review.value?.liked}`,
)
</script>

<style scoped>
.campsite {
  overflow-x: hidden;
  width: 100vw;
}

[data-menu-open='true'] .campsite,
.menu-open .campsite {
  overflow-x: unset;
}

.campsite :deep(.cs-photos) {
  position: relative;
  width: 100%;
}

.campsite :deep(.cs-favourite) {
  position: absolute;
  right: 0.75em;
  top: 0.75em;
}

.campsite :deep(.campsite-rating) {
  top: -0.25em;
  right: 0.9em;
  position: absolute;
  font-size: 1em;
}

.campsite :deep(.campsite-rating.rating-bubble:before) {
  font-size: var(--tiny);
  color: var(--colour-text-default);
}

.campsite :deep(.cs-card-content) {
  flex-grow: 1;
  margin: 0.75em 0 1em 0;
  padding: 0 1.4em 0;
  position: relative;
  text-align: left;
  width: 100%;
}

.campsite :deep(.cs-name) {
  font-size: 1.5em;
  line-height: 1.15;
  margin: 0 1.3em 0 0;
  overflow-wrap: break-word;
}

.campsite :deep(.nutshells) {
  margin: 1em 0 0 0;
}

.campsite-location {
  font-size: var(--body);
  color: var(--colour-text-light);
}

.last-booked-wrapper {
  text-align: center;
  display: block;
}

.campsite-footer-breadcrumbs {
  padding: 2em 4% 1em;
}

/* desktop & tablet */
.campsite.desktop {
  width: 100%;
  overflow: visible;
}

.campsite.desktop :deep(.cs-favourite.favourite-link svg) {
  width: 2em;
  height: auto;
}

.desktop .campsite-header-breadcrumbs {
  background: white;
  margin: auto;
  max-width: var(--page-width-max);
  padding: 0 var(--page-width-padding);
}

.campsite.desktop
  :deep(.campsite-subsection:not(.cs-description):not(.pitchtypes-section)) {
  margin: auto;
  max-width: var(--page-width-max);
  padding: 1em var(--page-width-padding);
  display: block;
  font-size: inherit;
}

.campsite.desktop :deep(.cs-promo-page) {
  border-top: 4px solid var(--colour-page-background);
}

.campsite.desktop :deep(.campsite-subsection.ui-collapse .ui-collapse-header) {
  padding-left: 0;
}

.campsite.desktop :deep(.campsite-subsection .ui-collapse-header:active) {
  background: inherit;
}

.campsite.desktop :deep(.campsite-subsection .ui-collapse-content) {
  padding: 1.4em 0 0 0;
  font-size: inherit;
}

.campsite.desktop :deep(.campsite-subsection > header .caret),
.campsite.desktop
  :deep(.campsite-subsection > .show-checkbox:checked ~ header .caret),
.campsite.desktop :deep(.campsite-subsection:not(.cs-description) > footer) {
  display: none;
}

.campsite.desktop :deep(.campsite-subsection h2) {
  font-size: 2.25em;
}

.campsite.desktop :deep(.category-breadcrumbs) {
  padding-left: 0;
  font-size: inherit;
}

.campsite.desktop :deep(.nutshells) {
  margin: 0 0 0 1em;
}

.campsite.desktop :deep(.nutshells li) {
  font-size: inherit;
}

.campsite.desktop :deep(.zero-availability-msg) {
  align-items: center;
  background-color: #000;
  border-radius: 0.5em;
  color: white;
  display: inline-flex;
  justify-items: flex-start;
  line-height: 1.1;
  margin: auto;
  padding: 0.2em 0.5em;
  column-gap: 0.5em;
}

.campsite.desktop :deep(.no-availability-message) {
  padding: 1em var(--page-width-padding);
  margin: auto;
}

/* > 1600 */
@media only screen and (min-width: 1600px) {
  .campsite.desktop :deep(.no-availability-message) {
    padding: 1em var(--page-width-padding) 1em
      calc((100vw - var(--page-width-max)) / 2 + var(--page-width-padding));
  }
}
</style>

<style>
@import url('@/assets/css/breadcrumbs.css');
</style>
